import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import { Logo } from '../components';
import { ArrowRight, ArrowUpRight, ChevronUp, Calendar, Linkedin } from 'lucide-react';
import { servicesSeedData } from '../database/seedData/services';
import { linkedInPointOfViews } from '../database/seedData/pointOfViews';
import ServiceCatalogCard from '../components/ServiceCatalogCard';
import { OperatingDisciplinesDiagram } from '../components/executive';

const heroOperatingPillars = [
  {
    label: 'Strategy',
    description: 'Where AI investment ties to capital discipline and operating priorities.',
  },
  {
    label: 'Governance',
    description: 'Authority, evidence, and named ownership before anything reaches production.',
  },
  {
    label: 'Architecture',
    description: 'Data and systems that can support agents and analytics at scale.',
  },
  {
    label: 'Delivery',
    description: 'Bounded pilots with oversight—not open-ended experimentation.',
  },
];

const formatDate = (dateString) => {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
};

const PointOfViewCard = ({ pov, theme }) => {
  const isLinkedIn = (pov.source || 'LinkedIn') === 'LinkedIn';
  const badgeLabel = isLinkedIn ? 'LinkedIn' : 'Shared Oxygen';
  const ctaLabel = isLinkedIn ? 'Read On LinkedIn' : 'Read On Shared Oxygen';

  return (
  <a
    href={pov.url}
    target="_blank"
    rel="noopener noreferrer"
    className={`group flex flex-col h-full rounded-3xl border overflow-hidden transition-all ${
      theme === 'dark'
        ? 'bg-slate-900/50 border-slate-800 hover:border-slate-600 hover:shadow-lg hover:shadow-indigo-500/5'
        : 'bg-white border-slate-300 shadow-md hover:shadow-xl hover:border-slate-400'
    }`}
  >
    <div className={`relative h-52 sm:h-56 overflow-hidden flex items-center justify-center ${theme === 'dark' ? 'bg-slate-950' : 'bg-slate-900'}`}>
      <img
        src={pov.imageUrl}
        alt={pov.imageAlt || pov.title}
        className="w-full h-full object-contain p-2 transition-transform duration-500 group-hover:scale-[1.02]"
      />
      <div
        className={`absolute top-4 left-4 inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-white text-xs font-semibold shadow-md ${
          isLinkedIn ? 'bg-[#0A66C2]' : 'bg-slate-800'
        }`}
      >
        {isLinkedIn ? <Linkedin className="w-3.5 h-3.5" aria-hidden /> : null}
        {badgeLabel}
      </div>
    </div>
    <div className="flex flex-col flex-1 p-6">
      <div className={`flex items-center gap-2 text-xs mb-3 ${theme === 'dark' ? 'text-slate-400' : 'text-slate-600'}`}>
        <Calendar className="w-3.5 h-3.5" aria-hidden />
        <span>{formatDate(pov.publishedDate)}</span>
        <span aria-hidden>·</span>
        <span>{pov.author}</span>
      </div>
      <h3
        className={`text-xl font-semibold mb-3 leading-snug group-hover:text-blue-600 transition-colors ${
          theme === 'dark' ? 'text-white' : 'text-slate-950'
        }`}
      >
        {pov.title}
      </h3>
      <p className={`text-sm leading-relaxed flex-1 line-clamp-4 ${theme === 'dark' ? 'text-slate-300' : 'text-slate-700'}`}>
        {pov.excerpt}
      </p>
      <div className="mt-5 pt-5 border-t border-slate-300 dark:border-slate-700/80 text-sm font-semibold text-blue-600 inline-flex items-center gap-2">
        {ctaLabel}
        <ArrowUpRight className="w-4 h-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
      </div>
    </div>
  </a>
  );
};

const ServiceCard = ({ service }) => {
  const navigate = useNavigate();
  const { theme } = useTheme();

  const handleClick = () => {
    console.log('ServiceCard clicked:', service);

    // Validate service object and slug
    if (!service) {
      console.error('Invalid service object');
      return;
    }

    if (!service.slug) {
      console.error('Service is missing slug', service);
      return;
    }

    // Normalize slug to ensure it's URL-friendly
    const safeSlug = service.slug.toLowerCase().replace(/[^a-z0-9-]/g, '-');

    console.log('Navigating to service:', `/services/${safeSlug}`);
    navigate(`/services/${safeSlug}`);
  };

  // Fallback if service is incomplete
  if (!service || !service.title) {
    return null;
  }

  // Professional icon mapping
  const iconMap = {
    '⬡': '◈', // Hexagon to diamond for Data Strategy
    '⬢': '◉', // Filled hexagon to concentric circle for AI Advisory
    '◬': '◊', // Angular shape to diamond outline for Generative AI
    '⬟': '◇', // Octagonal to diamond for Data Governance
    '⬠': '◍'  // Geometric shape to circled dot for Realtime Integration
  };

  const displayIcon = iconMap[service.icon] || service.icon;

  return (
    <div
      className={`p-6 rounded-xl transition-all duration-300 transform hover:-translate-y-2 cursor-pointer group relative overflow-hidden ${theme === 'dark'
        ? 'bg-gray-800 border-gray-700 shadow-lg hover:shadow-xl shadow-black/10'
        : 'bg-white border-gray-100 shadow-md hover:shadow-lg'
        } border`}
      onClick={handleClick}
    >
      {/* Vertical accent bar */}
      <div className="absolute left-0 top-0 w-1.5 h-full bg-gradient-to-b from-blue-400 to-indigo-600"></div>

      <div className="text-4xl mb-5 text-center opacity-80 font-light bg-clip-text text-transparent bg-gradient-to-r from-blue-400 to-indigo-500">
        {displayIcon}
      </div>
      <h3 className={`text-xl font-semibold mb-3 text-center group-hover:text-blue-500 transition-colors ${theme === 'dark' ? 'text-gray-100' : 'text-gray-800'
        }`}>
        {service.title}
      </h3>
      <p className={`text-center mb-5 line-clamp-3 text-sm ${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'
        }`}>
        {service.shortDescription || service.description || 'No description available'}
      </p>
      <div className="text-blue-400 text-center group-hover:text-blue-500 transition-colors text-sm flex items-center justify-center">
        <span>Explore Service</span>
        <ArrowRight className="ml-1 h-3.5 w-3.5 group-hover:translate-x-1 transition-transform duration-300" />
      </div>
    </div>
  );
};

// Metrics removed - no fabricated data

const advisoryPillars = [
  {
    title: 'Strategy & Mandate',
    description: 'Where AI and data investment ties to capital discipline, ownership, and risk.',
  },
  {
    title: 'Governance & Architecture',
    description: 'Judgment on platforms, data estates, and controls before commitments harden.',
  },
  {
    title: 'Evidence & Cadence',
    description: 'How leadership reviews progress—without prescribing a fixed delivery model.',
  },
];

const Home = () => {
  const navigate = useNavigate();
  const [showScrollTop, setShowScrollTop] = useState(false);
  const { theme } = useTheme();

  useEffect(() => {
    const handleScroll = () => {
      setShowScrollTop(window.scrollY > 300);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className={theme === 'dark' ? 'bg-slate-950 text-slate-100' : 'bg-white text-slate-800'}>
      {/* Executive Hero */}
      <section className={`relative overflow-hidden ${theme === 'dark' ? 'bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950' : 'bg-gradient-to-br from-white via-slate-50 to-blue-50'} py-20 lg:py-28`}>
        <div className="absolute inset-0 opacity-15 bg-[radial-gradient(circle_at_top,_rgba(37,99,235,0.35),_transparent_55%)]" />
        <div className="relative max-w-7xl mx-auto px-6 text-center xl:text-left space-y-8">
          <p className={`uppercase tracking-[0.4em] text-xs font-semibold ${theme === 'dark' ? 'text-slate-400' : 'text-slate-700'}`}>Shared Oxygen Advisory</p>
          <div className="grid xl:grid-cols-2 gap-10 xl:gap-14 items-stretch">
            <div className="flex flex-col justify-center">
              <h1 className={`text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-bold leading-tight tracking-tight ${theme === 'dark' ? 'text-white' : 'text-slate-950'}`}>
                AI That Answers To Enterprise Governance.
              </h1>
              <p className={`mt-6 text-xl lg:text-2xl leading-relaxed ${theme === 'dark' ? 'text-slate-200' : 'text-slate-800'}`}>
                Align AI investment to capital discipline, risk ownership, and measurable operating value.
              </p>
              <div className="mt-10 flex flex-col xl:flex-row gap-4">
                <button 
                  onClick={() => navigate('/contact')} 
                  className={`px-10 py-4 rounded-md text-base font-semibold transition-all ${
                    theme === 'dark' 
                      ? 'bg-white text-slate-900 hover:bg-slate-50' 
                      : 'bg-slate-900 text-white hover:bg-slate-800'
                  }`}
                >
                  Schedule a Conversation
                </button>
                <a
                  href={`/Shared-Oxygen-Navigator-standalone.html?theme=${theme}`}
                  className={`px-10 py-4 rounded-md text-base font-semibold border-2 transition-all ${
                    theme === 'dark' 
                      ? 'border-slate-700 text-slate-200 hover:bg-slate-900/60' 
                      : 'border-slate-400 text-slate-900 hover:bg-slate-100'
                  }`}
                >
                  Explore AI Governance Navigator
                </a>
              </div>
            </div>
            <div
              className={`flex h-full flex-col rounded-2xl border overflow-hidden ${
                theme === 'dark'
                  ? 'bg-slate-900/60 border-slate-800'
                  : 'bg-white border-slate-300 shadow-xl shadow-slate-900/10'
              }`}
            >
              <div
                className={`px-6 sm:px-8 pt-7 sm:pt-8 pb-6 border-b ${
                  theme === 'dark' ? 'border-slate-800 bg-slate-950/40' : 'border-slate-200 bg-slate-50'
                }`}
              >
                <p
                  className={`text-xs uppercase tracking-[0.35em] mb-3 font-semibold ${
                    theme === 'dark' ? 'text-slate-400' : 'text-slate-600'
                  }`}
                >
                  Executive Operating Model
                </p>
                <p
                  className={`text-lg leading-relaxed ${
                    theme === 'dark' ? 'text-slate-100' : 'text-slate-900'
                  }`}
                >
                  Four disciplines leadership uses to govern AI—from investment through production.
                </p>

                {/* Flow rail */}
                <div className="mt-6 flex items-center gap-1" aria-hidden>
                  {heroOperatingPillars.map((pillar, index) => (
                    <React.Fragment key={pillar.label}>
                      <div className="flex flex-col items-center gap-1.5 flex-1 min-w-0">
                        <div
                          className={`h-1 w-full rounded-full ${
                            theme === 'dark' ? 'bg-blue-500/80' : 'bg-blue-600'
                          }`}
                        />
                        <span
                          className={`text-[9px] sm:text-[10px] uppercase tracking-[0.08em] w-full text-center font-medium whitespace-nowrap ${
                            theme === 'dark' ? 'text-slate-500' : 'text-slate-600'
                          }`}
                        >
                          {pillar.label}
                        </span>
                      </div>
                      {index < heroOperatingPillars.length - 1 && (
                        <div
                          className={`w-2 h-px flex-shrink-0 ${
                            theme === 'dark' ? 'bg-slate-700' : 'bg-slate-400'
                          }`}
                        />
                      )}
                    </React.Fragment>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 flex-1">
                {heroOperatingPillars.map((pillar, index) => (
                  <div
                    key={pillar.label}
                    className={`group relative p-5 2xl:p-7 flex flex-col gap-3 transition-colors ${
                      index % 2 === 0
                        ? theme === 'dark'
                          ? 'sm:border-r border-b border-slate-800'
                          : 'sm:border-r border-b border-slate-200'
                        : theme === 'dark'
                          ? 'border-b border-slate-800'
                          : 'border-b border-slate-200'
                    } ${
                      index >= 2
                        ? theme === 'dark'
                          ? 'sm:border-b-0'
                          : 'sm:border-b-0'
                        : ''
                    } ${theme === 'dark' ? 'hover:bg-slate-800/30' : 'hover:bg-slate-50'}`}
                  >
                    <div className="absolute left-0 top-6 bottom-6 w-0.5 bg-gradient-to-b from-blue-400 to-indigo-500 opacity-0 group-hover:opacity-100 transition-opacity" />
                    <div className="flex items-baseline gap-3">
                      <span
                        className={`text-2xl font-bold tabular-nums leading-none ${
                          theme === 'dark' ? 'text-blue-500/40' : 'text-blue-600/70'
                        }`}
                      >
                        {String(index + 1).padStart(2, '0')}
                      </span>
                      <span
                        className={`text-sm font-semibold tracking-wide ${
                          theme === 'dark' ? 'text-blue-300' : 'text-blue-800'
                        }`}
                      >
                        {pillar.label}
                      </span>
                    </div>
                    <p
                      className={`text-sm leading-relaxed ${
                        theme === 'dark' ? 'text-slate-300' : 'text-slate-700'
                      }`}
                    >
                      {pillar.description}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Executive Metrics section removed - no fabricated data */}

      {/* Service Spotlight */}
      <section className="py-20">
        <div className="max-w-6xl mx-auto px-6">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6 mb-12">
            <div>
              <p className={`text-xs uppercase tracking-[0.4em] mb-3 font-semibold ${theme === 'dark' ? 'text-slate-400' : 'text-slate-700'}`}>Advisory Portfolio</p>
              <h2 className={`text-3xl lg:text-4xl font-semibold leading-tight ${theme === 'dark' ? 'text-white' : 'text-slate-950'}`}>
                Independent Perspective, Informed By Experience
              </h2>
            </div>
              <button onClick={() => navigate('/services')} className="inline-flex items-center gap-2 text-sm font-semibold text-blue-700 hover:text-blue-800">
              View Services <ArrowRight className="w-4 h-4" />
            </button>
          </div>
          <div className="grid lg:grid-cols-3 gap-8">
            {servicesSeedData.slice(0, 3).map((service) => (
              <ServiceCatalogCard
                key={service.slug}
                service={service}
                theme={theme}
                ctaLabel="View service"
                onClick={() => navigate(`/services/${service.slug}`)}
              />
            ))}
          </div>
        </div>
      </section>

      {/* Latest points of view — LinkedIn */}
      <section className={`py-20 ${theme === 'dark' ? 'bg-slate-900' : 'bg-slate-100'}`}>
        <div className="max-w-6xl mx-auto px-6">
          <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-10">
            <div>
              <p className={`text-xs uppercase tracking-[0.4em] mb-3 font-semibold ${theme === 'dark' ? 'text-slate-400' : 'text-slate-700'}`}>
                Executive Briefing
              </p>
              <h2 className={`text-3xl font-semibold ${theme === 'dark' ? 'text-white' : 'text-slate-950'}`}>
                Latest Points Of View
              </h2>
            </div>
            <button
              onClick={() => navigate('/blog')}
              className="text-sm font-semibold text-blue-700 hover:text-blue-800 flex items-center gap-2 self-start sm:self-auto"
            >
              View Insights Archive <ArrowRight className="w-4 h-4" />
            </button>
          </div>
          <div className="grid md:grid-cols-2 gap-6 lg:gap-8">
            {linkedInPointOfViews.map((pov) => (
              <PointOfViewCard key={pov.id} pov={pov} theme={theme} />
            ))}
          </div>
        </div>
      </section>

      {/* Advisory Pillars */}
      <section className="py-20">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-14">
            <p className={`text-xs uppercase tracking-[0.4em] mb-4 font-semibold ${theme === 'dark' ? 'text-slate-400' : 'text-slate-700'}`}>Operating System</p>
            <h2 className={`text-3xl font-semibold ${theme === 'dark' ? 'text-white' : 'text-slate-950'}`}>Engagements Anchored In Three Disciplines</h2>
          </div>
          <div className="max-w-4xl mx-auto mb-14">
            <OperatingDisciplinesDiagram theme={theme} />
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {advisoryPillars.map((pillar) => (
              <div key={pillar.title} className={`${theme === 'dark' ? 'bg-slate-950/40 border border-slate-800' : 'bg-white border border-slate-300 shadow-md'} rounded-2xl p-8 border-t-2 border-t-blue-600`}>
                <h3 className={`text-xl font-semibold mb-3 ${theme === 'dark' ? '' : 'text-slate-950'}`}>{pillar.title}</h3>
                <p className={`text-sm leading-relaxed ${theme === 'dark' ? 'text-slate-300' : 'text-slate-700'}`}>{pillar.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className={`${theme === 'dark' ? 'bg-gradient-to-r from-blue-900 via-indigo-900 to-slate-900' : 'bg-gradient-to-r from-blue-600 via-indigo-600 to-slate-900'} py-20`}>
        <div className="max-w-4xl mx-auto px-6 text-center text-white">
          <p className="uppercase tracking-[0.4em] text-xs mb-6 text-white/60">Partner Engagement</p>
          <h2 className="text-3xl lg:text-4xl font-semibold mb-6 text-white">AI That Answers To Enterprise Governance.</h2>
              <p className="text-lg text-white/80 mb-8">Share the mandate and constraints. An initial conversation to understand what counsel would be useful.</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button onClick={() => navigate('/contact')} className="px-8 py-3 rounded-full bg-white text-slate-900 font-semibold">Start a Conversation</button>
            <button onClick={() => navigate('/services')} className="px-8 py-3 rounded-full border border-white/40 text-white font-semibold hover:bg-white/10">Review Services</button>
          </div>
        </div>
      </section>

      {/* Scroll to top */}
      {showScrollTop && (
        <button
          onClick={scrollToTop}
          className={`fixed bottom-8 right-8 p-3 rounded-full shadow-lg border z-50 ${theme === 'dark' ? 'bg-gray-800/80 text-blue-400 border-gray-700 hover:bg-gray-700' : 'bg-white text-blue-700 border-slate-300 hover:bg-blue-50'}`}
          aria-label="Scroll To Top"
        >
          <ChevronUp className="h-5 w-5" />
        </button>
      )}
    </div>
  );
};

export default Home;
